import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RefreshCw, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { getRoles } from '@/lib/services/rbacService'
import type { RoleListItem } from '@/lib/types/api'

interface ChangeRoleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  roleId: string | null
  onRoleChange: (roleId: string) => void
  onSubmit: () => void
  isLoading: boolean
}

export function ChangeRoleDialog({
  open,
  onOpenChange,
  roleId,
  onRoleChange,
  onSubmit,
  isLoading,
}: ChangeRoleDialogProps) {
  const { t } = useTranslation()

  // Fetch available roles from RBAC API
  const {
    data: rolesData,
    isLoading: isLoadingRoles,
    error: rolesError,
  } = useQuery({
    queryKey: ['roles-for-assignment'],
    queryFn: () => getRoles(),
    enabled: open, // Only fetch when dialog is open
  })

  const roles = rolesData?.roles || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.users.changeRole')}</DialogTitle>
          <DialogDescription>{t('admin.users.changeRoleDescription')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {rolesError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{t('admin.users.roleLoadError')}</AlertDescription>
            </Alert>
          )}
          <div>
            <Label htmlFor="role">{t('admin.users.role')}</Label>
            <Select
              value={roleId || ''}
              onValueChange={onRoleChange}
              disabled={isLoadingRoles || isLoading}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    isLoadingRoles ? t('admin.users.loading') : t('admin.users.selectRole')
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role: RoleListItem) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                    {role.isSystem && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({t('admin.rbac.systemRole')})
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t('admin.users.cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={isLoading || isLoadingRoles || !roleId}>
            {isLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('admin.users.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
